import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ConfigProvider, theme as antdTheme } from "antd";
import zhCN from "antd/locale/zh_CN";
import enUS from "antd/locale/en_US";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import "dayjs/locale/en";
import { LocaleProvider, useLocale } from "./contexts/LocaleContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import App from "./App";
import "./index.css";

function AppWithLocale() {
  const { locale } = useLocale();
  const { theme } = useTheme();

  dayjs.locale(locale === "en" ? "en" : "zh-cn");

  const antdThemeConfig =
    theme === "dark"
      ? {
          algorithm: antdTheme.darkAlgorithm,
          token: {
            colorBgContainer: "#1E293B",
            colorBgElevated: "#1E293B",
            colorBgLayout: "#0F172A",
            colorBorder: "#334155",
          },
        }
      : theme === "tech"
      ? {
          algorithm: antdTheme.darkAlgorithm,
          token: {
            colorPrimary: "#00ffff",
            colorBgContainer: "#0f1629",
            colorBgElevated: "#0f1629",
            colorBgLayout: "#0a0e27",
            colorText: "#ffffff",
            colorTextSecondary: "#b8c5d6",
            colorTextTertiary: "#6b7a99",
            colorBorder: "#00ffff",
            colorBorderSecondary: "rgba(0, 255, 255, 0.4)",
            colorPrimaryBg: "#0f1629",
            colorPrimaryBgHover: "#1a2332",
            colorPrimaryBorder: "#00ffff",
            colorPrimaryBorderHover: "#00bfff",
            colorPrimaryHover: "#00bfff",
            colorPrimaryActive: "#00ffff",
            colorLink: "#00ffff",
            colorLinkHover: "#00bfff",
            colorLinkActive: "#00ffff",
            colorText: "#ffffff",
            colorTextSecondary: "#b8c5d6",
            colorFill: "rgba(0, 255, 255, 0.1)",
            colorFillSecondary: "rgba(0, 255, 255, 0.05)",
            colorFillTertiary: "rgba(0, 255, 255, 0.02)",
            colorFillQuaternary: "rgba(0, 255, 255, 0.01)",
          },
          components: {
            Button: {
              defaultBg: "#0f1629",
              defaultColor: "#00ffff",
              defaultBorderColor: "#00ffff",
              defaultHoverBg: "#1a2332",
              defaultHoverColor: "#00bfff",
              defaultHoverBorderColor: "#00bfff",
              defaultActiveBg: "#1a2332",
              defaultActiveColor: "#00ffff",
              defaultActiveBorderColor: "#00ffff",
              primaryColor: "#0a0e27",
              primaryBg: "#00ffff",
              primaryBorderColor: "#00ffff",
              primaryHoverBg: "#00bfff",
              primaryHoverBorderColor: "#00bfff",
              linkHoverBg: "transparent",
              colorText: "#00ffff",
              colorTextSecondary: "#00ffff",
            },
            Modal: {
              contentBg: "#0f1629",
              headerBg: "#0f1629",
              titleColor: "#ffffff",
              colorText: "#ffffff",
              colorTextHeading: "#ffffff",
              colorTextSecondary: "#ffffff",
              colorTextTertiary: "#ffffff",
              colorBorder: "rgba(0, 255, 255, 0.2)",
              colorBorderSecondary: "rgba(0, 255, 255, 0.4)",
              boxShadow: "0 0 20px rgba(0, 255, 255, 0.3), 0 0 40px rgba(0, 191, 255, 0.2)",
            },
          },
        }
      : {
          token: {
            colorBgLayout: "#F8FAFC",
          },
        };

  return (
    <ConfigProvider locale={locale === "en" ? enUS : zhCN} theme={antdThemeConfig}>
      <App />
    </ConfigProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <LocaleProvider>
        <AppWithLocale />
      </LocaleProvider>
    </ThemeProvider>
  </StrictMode>
);
